import { useCallback, useMemo } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { PageTitle } from '@components/PageTitle';
import { Accordion } from '@components/ui/accordion';
import { EQueryKeys } from '@enums/queryKeys.enum';
import { dateFormatter } from '@formatters/date.formatter';
import { treatmentSearchSchema } from '@schemas/treatmentSearch.schema';
import { medicationCreatesService } from '@services/medicationCreates/service';
import { medicationUpdatesService } from '@services/medicationUpdates/service';

import { MyTreatmentsItem } from '../my-treatments/@components/Item';
import { MyTreatmentsSearchBar } from '../my-treatments/@components/SearchBar';

const myMedicationUpdatesQueryOptions = ({
  page,
  title,
}: {
  page: number;
  title?: string;
}) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.MyMedicationUpdates, title, page],
    queryFn: () =>
      medicationUpdatesService.getAllByCollaborator({
        page: page,
        title,
      }),
  });

const myMedicationCreatesQueryOptions = ({
  page,
  title,
}: {
  page: number;
  title?: string;
}) =>
  queryOptions({
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60,
    queryKey: [EQueryKeys.MyMedicationCreates, title, page],
    queryFn: () =>
      medicationCreatesService.getAllByCollaborator({
        page: page,
        title,
      }),
  });

export const Route = createFileRoute('/_layout/(private)/my-medications/')({
  component: MyMedications,
  validateSearch: search => treatmentSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({
    page: search.page,
    title: search.title || undefined,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(
      myMedicationUpdatesQueryOptions({ title: deps.title, page: deps.page }),
    );
    await context.queryClient.ensureQueryData(
      myMedicationCreatesQueryOptions({ title: deps.title, page: deps.page }),
    );
  },
});

function MyMedications() {
  const navigate = Route.useNavigate();
  const { page, title } = Route.useSearch();
  const { data: medicationUpdates } = useQuery(
    myMedicationUpdatesQueryOptions({ title, page }),
  );
  const { data: medicationCreates } = useQuery(
    myMedicationCreatesQueryOptions({ title, page }),
  );

  const handleSearch = useCallback(
    (value: string) => {
      navigate({
        search: prev => ({
          ...prev,
          title: value || undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const reviewList = useMemo(() => {
    return (
      medicationUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          medicationCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'EM_REVISAO') || []
    );
  }, [medicationCreates?.list, medicationUpdates?.list]);

  const finishList = useMemo(() => {
    return (
      medicationUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          medicationCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'CONCLUIDO') || []
    );
  }, [medicationCreates?.list, medicationUpdates?.list]);

  const correctionList = useMemo(() => {
    return (
      medicationUpdates?.list
        .map(item => ({ ...item, type: 'UPDATE' }))
        .concat(
          medicationCreates?.list.map(item => ({ ...item, type: 'CREATE' })) ||
            [],
        )
        .filter(item => item.status === 'EM_CORRECAO') || []
    );
  }, [medicationCreates?.list, medicationUpdates?.list]);

  return (
    <div>
      <PageTitle title="Meus medicamentos" />

      <MyTreatmentsSearchBar
        label="Pesquisar medicamento"
        onSubmit={handleSearch}
        placeholder="Ex: Amoxicilina"
      />

      <Accordion.Root className="grid gap-4" type="multiple">
        <MyTreatmentsItem
          kind="medication"
          status="Em revisão"
          treatments={medicationUpdates?.list
            .filter(item => item.status === 'EM_PRODUCAO')
            .map(item => ({
              status: 'Em revisão',
              id: item.id,
              title: item.title,
              gpCoins: item.amount,
              startedAt: dateFormatter(item.startedAt),
              expiredAt: dateFormatter(item.expiredAt),
              type: 'UPDATE',
            }))}
        />
        <MyTreatmentsItem
          kind="medication"
          status="Em criação"
          treatments={medicationCreates?.list
            .filter(item => item.status === 'EM_PRODUCAO')
            .map(item => ({
              status: 'Em criação',
              id: item.id,
              title: item.title,
              gpCoins: item.amount,
              startedAt: dateFormatter(item.startedAt),
              expiredAt: dateFormatter(item.expiredAt),
              type: 'CREATE',
            }))}
        />
        <MyTreatmentsItem
          kind="medication"
          status="Em aprovação"
          treatments={reviewList.map(item => ({
            status: 'Em aprovação',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: null,
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
        <MyTreatmentsItem
          kind="medication"
          status="Em correção"
          treatments={correctionList.map(item => ({
            status: 'Em correção',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: dateFormatter(item.expiredAt),
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
        <MyTreatmentsItem
          kind="medication"
          status="Concluído"
          treatments={finishList.map(item => ({
            status: 'Concluído',
            id: item.id,
            title: item.title,
            gpCoins: item.amount,
            startedAt: dateFormatter(item.startedAt),
            expiredAt: null,
            type: item.type as 'UPDATE' | 'CREATE',
          }))}
        />
      </Accordion.Root>
    </div>
  );
}
